import mqtt from "mqtt";
import eventBus from "@/src/utils/eventBus.js";
import WorkspaceService from "@/src/api/WorkspaceService.js";
import DashboardService from "@/src/api/DashboardService.js";
import { primeSSLConnection } from "@/src/utils/mqttSSLPriming.js";

class MqttService {
  constructor() {
    this.client = null;
    this.isConnected = false;
    this.subscriptions = new Map();
    this.messageHandlers = new Map();
    this.credentialsType = null;
    this.hasConnectedBefore = false;
  }

  async initialize(credentialsType = "default") {
    try {
      if (
        this.client &&
        this.isConnected &&
        this.credentialsType === credentialsType
      ) {
        console.log("MQTT already connected with same credentials type");
        return this.client;
      }

      if (this.client && this.credentialsType !== credentialsType) {
        console.log(
          "Disconnecting existing MQTT connection for different credentials",
        );
        this.disconnect();
      }

      const credentials = await this.fetchMqttCredentials(credentialsType);
      if (!credentials) {
        throw new Error("Failed to fetch MQTT credentials");
      }

      // CRITICAL FIX: Prime SSL/TLS session for WSS connections
      // This fixes the issue where MQTT works after running diagnostic tools
      await primeSSLConnection(credentials.url);

      const clientId = this.generateClientId();

      this.client = mqtt.connect(credentials.url, {
        clientId,
        username: credentials.username,
        password: credentials.password,
        reconnectPeriod: 5000,
        connectTimeout: 30000,
        keepalive: 60,
        clean: true,
        rejectUnauthorized: false,
        protocolVersion: 4,
        wsOptions: {
          rejectUnauthorized: false,
          headers: {
            "User-Agent": "SuperSales-MQTT-Client",
            Origin: window.location.origin,
          },
          protocol: "mqtt",
        },
      });

      this.credentialsType = credentialsType;
      this.setupClientEventHandlers();

      return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
          console.warn("MQTT connection timeout, continuing without MQTT");
          this.isConnected = false;
          resolve(null);
        }, 35000);

        this.client.on("connect", () => {
          clearTimeout(timeout);
          this.isConnected = true;
          console.log("MQTT connected successfully");
          resolve(this.client);
        });

        this.client.on("error", (error) => {
          clearTimeout(timeout);
          console.warn("MQTT connection error, continuing without MQTT:", error);
          this.isConnected = false;
          resolve(null);
        });
      });
    } catch (error) {
      console.error("Failed to initialize MQTT:", error);
      throw error;
    }
  }

  async fetchMqttCredentials(credentialsType = "default") {
    try {
      const response = await DashboardService.fetchMqttCredentials();
      if (response?.success) {
        return {
          username: response.username,
          password: response.password,
          url: response.url,
          user_id: response.user_id,
        };
      }
      return null;
    } catch (error) {
      console.error("Failed to fetch MQTT credentials:", error);
      return null;
    }
  }

  generateClientId() {
    const prefix =
      this.credentialsType === "plivo" ? "plivo_web_" : "sales_dev_";
    return prefix + Math.random().toString(16).substring(2, 8);
  }

  setupClientEventHandlers() {
    if (!this.client) return;

    this.client.on("connect", () => {
      const wasReconnecting = this.hasConnectedBefore && !this.isConnected;
      this.isConnected = true;
      this.hasConnectedBefore = true;

      if (wasReconnecting) {
        this.resubscribeExistingTopics();
        eventBus.emit("mqtt-reconnected");
      } else {
        eventBus.emit("mqtt-connected");
      }
    });

    this.client.on("disconnect", () => {
      this.isConnected = false;
      this.subscriptions.clear();
    });

    this.client.on("error", (error) => {
      this.isConnected = false;
    });

    this.client.on("message", (topic, message) => {
      this.handleMessage(topic, message);
    });

    this.client.on("reconnect", () => {
    });

    this.client.on("close", () => {
      this.isConnected = false;
      this.subscriptions.clear();
    });

    this.client.on("offline", () => {
      this.isConnected = false;
      this.subscriptions.clear();
    });
  }


  subscribe(topic, messageHandler = null) {
    return new Promise((resolve, reject) => {
      if (!this.client || !this.isConnected) {
        resolve(null);
        return;
      }

      this.client.subscribe(topic, { qos: 0 }, (err) => {
        if (err) {
          resolve(null);
        } else {
          this.subscriptions.set(topic, true);

          if (messageHandler) {
            this.registerMessageHandler(topic, messageHandler);
          }
          resolve(topic);
        }
      });
    });
  }

  unsubscribe(topic) {
    this.subscriptions.delete(topic);
    this.messageHandlers.delete(topic);

    return new Promise((resolve, reject) => {
      if (!this.client) {
        resolve(null);
        return;
      }

      this.client.unsubscribe(topic, (err) => {
        if (err) {
          console.error(`MQTT Unsubscription failed for: ${topic}`, err);
        }
        resolve(topic);
      });
    });
  }

  registerMessageHandler(topic, handler) {
    if (!this.messageHandlers.has(topic)) {
      this.messageHandlers.set(topic, []);
    }
    const handlers = this.messageHandlers.get(topic);
    if (!handlers.includes(handler)) {
      handlers.push(handler);
    }
  }

  removeMessageHandler(topic, handler) {
    if (this.messageHandlers.has(topic)) {
      const handlers = this.messageHandlers.get(topic);
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
      if (handlers.length === 0) {
        this.messageHandlers.delete(topic);
      }
    }
  }

  handleMessage(topic, message) {
    try {
      let foundHandlers = [];

      const exactHandlers = this.messageHandlers.get(topic);
      if (exactHandlers && exactHandlers.length > 0) {
        foundHandlers = [...exactHandlers];
      } else {
      }

      for (const [
        subscribedTopic,
        handlers,
      ] of this.messageHandlers.entries()) {
        if (subscribedTopic !== topic && handlers && handlers.length > 0) {
          if (this.topicMatches(subscribedTopic, topic)) {
            foundHandlers = [...foundHandlers, ...handlers];
          }
        }
      }


      if (foundHandlers.length > 0) {
        const parsedMessage = this.parseMessage(message);
        
        foundHandlers.forEach((handler, index) => {
          try {
            handler(parsedMessage, topic);
          } catch (error) {
            console.error(
              `Error in MQTT message handler for topic ${topic}:`,
              error,
            );
          }
        });
      } else {
        console.warn(`This message will NOT be processed!`);
      }
    } catch (error) {
      console.error(`Error handling MQTT message for topic ${topic}:`, error);
    }
  }

  topicMatches(pattern, topic) {
    const regexPattern = pattern.replace(/\+/g, "[^/]+").replace(/#/g, ".*");

    const regex = new RegExp(`^${regexPattern}$`);
    return regex.test(topic);
  }

  parseMessage(message) {
    try {
      const messageStr = message.toString();
      return JSON.parse(messageStr);
    } catch (error) {
      console.warn("Failed to parse MQTT message as JSON, returning as string");
      return message.toString();
    }
  }

  publish(topic, message, options = {}) {
    return new Promise((resolve, reject) => {
      if (!this.client || !this.isConnected) {
        reject(new Error("MQTT client not connected"));
        return;
      }

      const messageStr =
        typeof message === "string" ? message : JSON.stringify(message);

      const publishOptions = {
        qos: 0,
        retain: false,
        ...options,
      };

      this.client.publish(topic, messageStr, publishOptions, (err) => {
        if (err) {
          console.error(`Failed to publish to topic ${topic}:`, err);
          reject(err);
        } else {
          console.log(`Message published to topic: ${topic}`);
          resolve();
        }
      });
    });
  }

  resubscribeExistingTopics() {
    if (!this.client || !this.isConnected) return;

    const topics = Array.from(this.messageHandlers.keys());
    if (topics.length === 0) return;

    console.log(`[MqttService] Re-subscribing to ${topics.length} topics after reconnect`);
    topics.forEach((topic) => {
      this.client.subscribe(topic, { qos: 0 }, (err) => {
        if (err) {
          console.error(`[MqttService] Failed to re-subscribe to ${topic}:`, err);
        } else {
          this.subscriptions.set(topic, true);
        }
      });
    });
  }

  isClientConnected() {
    return this.isConnected && this.client && this.client.connected;
  }

  getSubscribedTopics() {
    return Array.from(this.subscriptions.keys());
  }

  disconnect() {
    if (this.client) {
      console.log("Disconnecting MQTT client...");
      this.client.end(true);
      this.isConnected = false;
      this.subscriptions.clear();
      this.messageHandlers.clear();
      this.credentialsType = null;
    }
  }

  reconnect() {
    if (this.client) {
      console.log("Reconnecting MQTT client...");
      this.client.reconnect();
    } else {
      console.log("Initializing MQTT connection...");
      return this.initialize(this.credentialsType);
    }
  }
}

export default new MqttService();
